// TDS2 SHIM — composes SystemErrorState; no direct TDS2 counterpart.
// Specialized error state for SFAP deploy failures. The backend raises
// `SfapClient::PublishError` with messages shaped like:
//   `SFAP publish HTTP 401: <json body>`
//   `SFAP compile HTTP 503: <json body>`
//   `SFAP version publish HTTP 401: <json body>`
//   `Activation failed: 503`
// And the chat controller may prefix that with `Deploy failed: ` when it
// surfaces the error inline. We parse out the operation, status code, and
// JSON body, surface the upstream message as the body, and tuck the full
// payload into a <details> disclosure.
import React from 'react';
import PropTypes from 'prop-types';
import SystemErrorState from './SystemErrorState';

// Patterns are intentionally narrow + bounded to avoid ReDoS:
//   - The operation class is letters only (no whitespace) so it can't overlap
//     with the surrounding \s+ separators. We allow one optional second word
//     to cover "version publish".
//   - All \s+ / \s* runs are capped with explicit upper bounds.
const DEPLOY_PREFIX_REGEX = /^Deploy failed:\s{0,8}/i;
const SFAP_REGEX = /^SFAP\s{1,4}([a-z]{1,16}(?: [a-z]{1,16})?)\s{1,4}HTTP\s{1,4}(\d{3})\s{0,4}:\s{0,4}/i;
const ACTIVATION_REGEX = /^Activation failed:\s{0,8}(\d{3})\b/i;

function parseDeployError(rawMessage) {
  if (!rawMessage) {
    return { operation: null, statusCode: null, payload: null, raw: null };
  }
  const stripped = rawMessage.replace(DEPLOY_PREFIX_REGEX, '');

  const sfapMatch = stripped.match(SFAP_REGEX);
  if (sfapMatch) {
    const operation = sfapMatch[1].trim().toLowerCase();
    const statusCode = parseInt(sfapMatch[2], 10);
    const jsonPart = stripped.slice(sfapMatch[0].length).trim();
    let payload = null;

    try {
      payload = JSON.parse(jsonPart);
    } catch (e) {
      payload = null;
    }
    return { operation, statusCode, payload, raw: jsonPart };
  }

  const activationMatch = stripped.match(ACTIVATION_REGEX);
  if (activationMatch) {
    return {
      operation: 'activation',
      statusCode: parseInt(activationMatch[1], 10),
      payload: null,
      raw: stripped,
    };
  }

  return { operation: null, statusCode: null, payload: null, raw: stripped };
}

function statusLabel(statusCode) {
  if (statusCode === 400) return '400 Bad request';
  if (statusCode === 401) return '401 Unauthorized';
  if (statusCode === 403) return '403 Forbidden';
  if (statusCode === 404) return '404 Not found';
  if (statusCode === 409) return '409 Conflict';
  if (statusCode === 429) return '429 Rate limited';
  if (statusCode && statusCode >= 500) return statusCode + ' Server error';
  if (statusCode) return String(statusCode);
  return null;
}

function operationLabel(operation) {
  if (!operation) return 'Deploy';
  if (operation === 'publish') return 'Publish';
  if (operation === 'compile') return 'Compile';
  if (operation === 'version publish') return 'Version publish';
  if (operation === 'activation') return 'Activation';
  return operation.replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildEyebrow({ operation, statusCode }) {
  // operationLabel always returns a non-empty string ('Deploy' is the
  // fallback when operation is null/unknown), so `op` is always truthy.
  const op = operationLabel(operation);
  const status = statusLabel(statusCode);
  if (status) return op + ' · ' + status;
  return op + ' failed';
}

function pickHumanMessage(payload) {
  if (!payload) return null;
  if (typeof payload.message === 'string' && payload.message.length > 0) {
    return payload.message;
  }
  if (typeof payload.error === 'string' && payload.error.length > 0) {
    return payload.error;
  }
  return null;
}

// Mirrors Agent::Skills::CompileAndDeploy::ORG_BOT_VERSION_LOCKED_CODE: the org
// refuses every new agent (a Salesforce upgrade lag), so the script isn't at fault.
const ORG_BOT_VERSION_LOCKED_CODE = 'org_bot_version_locked';
const ORG_BOT_VERSION_LOCKED_TITLE = "Your playground org can't save agents right now";
const ORG_BOT_VERSION_LOCKED_BODY =
  'Salesforce is temporarily blocking agent changes in your playground org. ' +
  "This isn't caused by your script. Try again later.";

function CircleAlertIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default function DeployErrorState({
  rawMessage,
  code,
  title = 'Deployment failed',
  retryLabel = 'Try again',
  onRetry,
}) {
  const parsed = parseDeployError(rawMessage);
  const eyebrow = buildEyebrow(parsed);
  const human = pickHumanMessage(parsed.payload);
  const orgLocked = code === ORG_BOT_VERSION_LOCKED_CODE;

  const bodyText =
    (orgLocked ? ORG_BOT_VERSION_LOCKED_BODY : null) ||
    human ||
    parsed.raw ||
    rawMessage ||
    'The deploy step returned an error.';

  const codeLine = parsed.payload && parsed.payload.requestId
    ? 'Request ID · ' + parsed.payload.requestId
    : null;

  const body = (
    <React.Fragment>
      <p>{bodyText}</p>
      {codeLine ? (
        <p className="tds2-agentforce-state-card__code-line">
          <code>{codeLine}</code>
        </p>
      ) : null}
    </React.Fragment>
  );

  const rawJson = parsed.payload
    ? JSON.stringify(parsed.payload, null, 2)
    : (parsed.raw || rawMessage || '');

  const details = rawJson ? (
    <details className="tds2-agentforce-state-card__disclosure">
      <summary>Show raw response</summary>
      <pre className="tds2-agentforce-state-card__pre">{rawJson}</pre>
    </details>
  ) : null;

  const actions = onRetry ? (
    <button
      type="button"
      className="tds2-button tds2-button--primary tds2-button--small"
      data-tds2-shim="Button"
      onClick={onRetry}
    >
      <span className="tds2-button__label">{retryLabel}</span>
    </button>
  ) : null;

  return (
    <SystemErrorState
      variant="danger"
      icon={<CircleAlertIcon />}
      eyebrow={eyebrow}
      title={orgLocked ? ORG_BOT_VERSION_LOCKED_TITLE : title}
      body={body}
      details={details}
      actions={actions}
    />
  );
}

DeployErrorState.propTypes = {
  rawMessage: PropTypes.string,
  code: PropTypes.string,
  title: PropTypes.string,
  retryLabel: PropTypes.string,
  onRetry: PropTypes.func,
};

export { parseDeployError, pickHumanMessage, buildEyebrow };
